import { useRecoilState } from 'recoil'
import {
    ContentAction,
    ContentActionView,
    ContentView,
    ProfileViewWithShadowBan
} from '../../api/client'
import {
    canUserLikeContentState,
    canUserReplyToContentState,
    canLabelSpamState,
    canHideContentState,
    canDeleteContentState,
    canBlockUserState,
    canMuteUserState,
    userState
} from '../../store/DomainStore'
import { getActions, isActionApplied } from '../content/content-card/actions'
import BlockButton from './buttons/BlockButton'
import DeleteButton from './buttons/DeleteButton'
import HideButton from './buttons/HideButton'
import LikeButton from './buttons/LikeButton'
import MuteButton from './buttons/MuteButton'
import ReadStatusButton from './buttons/ReadStatusButton'
import ReplyButton from './buttons/ReplyButton'
import SpamButton from './buttons/SpamButton'
import ViewConversationButton from './buttons/ViewConversationButton'
import ReadOthersStatusDisplay from './ReadOthersStatusDisplay'

type Props = {
    content: ContentView
    onActionClick: (actions: ContentActionView[], queued?: boolean) => void
    profileCache: ProfileViewWithShadowBan[]
    actionCacheMap: Map<number, ContentActionView[]>
    onMarkAsReadClick: (content: ContentView) => void
    onMarkAsUnreadClick: (content: ContentView) => void
    readReceiptLoading: boolean
    showReplyButton?: boolean
}

/**
 * This component is responsible for rendering the action buttons for a piece of content.
 */
function ActionRow(props: Props) {
    const {
        content,
        onActionClick,
        profileCache,
        actionCacheMap,
        onMarkAsReadClick,
        onMarkAsUnreadClick,
        readReceiptLoading,
        showReplyButton = true
    } = props

    const [user] = useRecoilState(userState)

    // SOMEDAY: DC - I don't *think* we need these states anymore - we should just be able to access the user directly. I haven't removed them as doing so would require time for testing that I don't have.
    const [canUserLikeContent] = useRecoilState(canUserLikeContentState)
    const [canUserReplyToContent] = useRecoilState(canUserReplyToContentState)
    const [canLabelSpam] = useRecoilState(canLabelSpamState)
    const [canHideContent] = useRecoilState(canHideContentState)
    const [canDeleteContent] = useRecoilState(canDeleteContentState)
    const [canBlockUser] = useRecoilState(canBlockUserState)
    const [canMuteUser] = useRecoilState(canMuteUserState)

    // Identify the relevant actions from the action cache map
    const allExistingContentActions = actionCacheMap.get(content.id) || []

    const deleted = isActionApplied(allExistingContentActions, ContentAction.Delete)
    const hidden = isActionApplied(allExistingContentActions, ContentAction.Hide)

    const isOwnContent = content.serviceAuthorId === content.account.serviceAccountId

    // Moderation actions render as disabled (with a tooltip) for users without the toggle,
    // rather than being hidden — this surfaces the upgrade path for Engage-only clients.
    const filterActionsByPermissions = (actionList: ContentAction[]) =>
        actionList.filter((action) => {
            if (action === ContentAction.Like && !canUserLikeContent) {
                return false
            }
            if (action === ContentAction.Reply && !canUserReplyToContent) {
                return false
            }
            if (action === ContentAction.ConfirmSpam && !canLabelSpam) {
                return false
            }
            return true
        })

    // Per-action disabled messages — surfaces the upgrade path when a specific
    // moderation action is unavailable on the user's plan.
    const hideDisabledMessage = !canHideContent
        ? 'Contact support to enable hiding content'
        : undefined
    const deleteDisabledMessage = !canDeleteContent
        ? 'Contact support to enable deleting content'
        : undefined
    const blockDisabledMessage = !canBlockUser
        ? 'Contact support to enable blocking users'
        : undefined
    const muteDisabledMessage = !canMuteUser ? 'Contact support to enable muting users' : undefined

    const actions = filterActionsByPermissions(
        getActions(
            content.socialMediaServiceName.toLowerCase(),
            deleted,
            content.isPublic,
            content.authorised,
            isOwnContent
        )
    )

    const undeletedActions = filterActionsByPermissions(
        getActions(
            content.socialMediaServiceName.toLowerCase(),
            false,
            content.isPublic,
            content.authorised,
            isOwnContent
        )
    )

    let engageDisabledMessage: string | undefined
    if (deleted) {
        engageDisabledMessage = 'Engage actions are disabled because this content was deleted.'
    } else if (hidden) {
        engageDisabledMessage =
            'Engage actions are disabled while this content is hidden. Unhide it first.'
    }

    const likeVisible = actions.includes(ContentAction.Like)
    const likeSupported = undeletedActions.includes(ContentAction.Like)
    const shouldRenderLikeButton = likeVisible || (!!engageDisabledMessage && likeSupported)

    const replyVisible = actions.includes(ContentAction.Reply)
    const replySupported = undeletedActions.includes(ContentAction.Reply)
    const shouldRenderReplyButton =
        showReplyButton && (replyVisible || (!!engageDisabledMessage && replySupported))

    const hasModerateActions =
        actions.includes(ContentAction.Hide) ||
        actions.includes(ContentAction.Delete) ||
        actions.includes(ContentAction.Block) ||
        actions.includes(ContentAction.Mute)

    const shouldRenderEngageDivider =
        (shouldRenderLikeButton || shouldRenderReplyButton) && hasModerateActions

    return (
        <div className="flex flex-wrap gap-1">
            <ViewConversationButton content={content} />

            {/* Engage actions */}

            {user.canUseReadStatus && (
                <>
                    <ReadOthersStatusDisplay content={content} />
                    <ReadStatusButton
                        content={content}
                        onMarkAsReadClick={onMarkAsReadClick}
                        onMarkAsUnreadClick={onMarkAsUnreadClick}
                        loading={readReceiptLoading}
                    />
                    <div className="mx-2 hidden border-l-2 @lg:block"></div>
                </>
            )}

            {shouldRenderLikeButton && (
                <LikeButton
                    contents={[content]}
                    onActionClick={onActionClick}
                    actions={allExistingContentActions}
                    // actionCacheMap={actionCacheMap}
                    disabled={!!engageDisabledMessage}
                    disabledMessage={engageDisabledMessage}
                />
            )}
            {shouldRenderReplyButton && (
                <ReplyButton
                    content={content}
                    onActionClick={onActionClick}
                    profileCache={profileCache}
                    actionCacheMap={actionCacheMap}
                    disabled={!!engageDisabledMessage}
                    disabledMessage={engageDisabledMessage}
                />
            )}
            {/* Dividing line separating engage and moderate actions */}
            {shouldRenderEngageDivider && <div className="mx-2 hidden border-l-2 @lg:block"></div>}
            {/* Moderate actions */}
            {actions.includes(ContentAction.Hide) && (
                <HideButton
                    content={content}
                    onActionClick={onActionClick}
                    actions={allExistingContentActions}
                    actionCacheMap={actionCacheMap}
                    disabled={!canHideContent}
                    disabledMessage={hideDisabledMessage}
                />
            )}
            {actions.includes(ContentAction.Delete) && (
                <DeleteButton
                    content={content}
                    onActionClick={onActionClick}
                    actions={allExistingContentActions}
                    disabled={!canDeleteContent}
                    disabledMessage={deleteDisabledMessage}
                />
            )}
            {actions.includes(ContentAction.Block) && (
                <BlockButton
                    content={content}
                    onActionClick={onActionClick}
                    actions={allExistingContentActions}
                    disabled={!canBlockUser}
                    disabledMessage={blockDisabledMessage}
                />
            )}
            {actions.includes(ContentAction.Mute) && (
                <MuteButton
                    content={content}
                    onActionClick={onActionClick}
                    actions={allExistingContentActions}
                    disabled={!canMuteUser}
                    disabledMessage={muteDisabledMessage}
                />
            )}
            {actions.includes(ContentAction.ConfirmSpam) && (
                <SpamButton
                    content={content}
                    onActionClick={onActionClick}
                    actions={allExistingContentActions}
                />
            )}
        </div>
    )
}

export default ActionRow
